import React, { useState } from "react";

const LicenseUploadStep = ({ onNext, onBack, formData }) => {
  const [frontLicenseFile, setFrontLicenseFile] = useState(null);
  const [backLicenseFile, setBackLicenseFile] = useState(null);
  const [errors, setErrors] = useState({ front: "", back: "", upload: "" });
  const [loading, setLoading] = useState(false);

  const backendBaseUrl = 'https://api.karrentals.ca';

  // Validate file type
  const validateFileType = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/heic"];
    return allowedTypes.includes(file.type);
  };

  // Upload a file to S3 using a pre-signed URL
  const uploadToS3 = async (file, email, purpose, endDate) => {
    try {
      const response = await fetch(`${backendBaseUrl}/generate-presigned-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: file.name,
          fileType: file.type,
          filePurpose: purpose, // Use "front" or "back"
          email,
          end_date: endDate, // Include the end_date in the payload
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate pre-signed URL");
      }

      const { url } = await response.json();

      const putResponse = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!putResponse.ok) {
        throw new Error(`S3 upload failed with status ${putResponse.status}`);
      }

      return url.split("?")[0]; // Return clean file URL
    } catch (err) {
      console.error("Error uploading to S3:", err);
      throw new Error("Failed to upload file to S3");
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    setErrors({ front: "", back: "", upload: "" }); // Reset errors
    let hasError = false;

    if (!frontLicenseFile) {
      setErrors((prev) => ({
        ...prev,
        front: "Please upload the front of the license.",
      }));
      hasError = true;
    } else if (!validateFileType(frontLicenseFile)) {
      setErrors((prev) => ({
        ...prev,
        front: "Invalid file type. Only JPG and PNG are allowed.",
      }));
      hasError = true;
    }

    if (!backLicenseFile) {
      setErrors((prev) => ({
        ...prev,
        back: "Please upload the back of the license.",
      }));
      hasError = true;
    } else if (!validateFileType(backLicenseFile)) {
      setErrors((prev) => ({
        ...prev,
        back: "Invalid file type. Only JPG and PNG are allowed.",
      }));
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    try {
      const email = formData.email; // Use the renter's email for folder structure
      const endDate = formData.end_date; // Include the booking end date
      const frontFileUrl = await uploadToS3(frontLicenseFile, email, "front", endDate);
      const backFileUrl = await uploadToS3(backLicenseFile, email, "back", endDate);

      onNext({
        frontLicenseFile: frontFileUrl,
        backLicenseFile: backFileUrl,
      });
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        upload: "File upload failed. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">License Upload</h2>
      <p className="text-gray-600 mb-6">
        Upload front and back pictures of the renter's Driver's License.
      </p>
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="w-full sm:w-1/2">
          <label className="block font-semibold mb-2">Front of License</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFrontLicenseFile(e.target.files[0])}
            className={`block w-full border rounded px-3 py-2 ${
              errors.front ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.front && (
            <p className="text-red-500 text-sm mt-1">{errors.front}</p>
          )}
        </div>
        <div className="w-full sm:w-1/2">
          <label className="block font-semibold mb-2">Back of License</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setBackLicenseFile(e.target.files[0])}
            className={`block w-full border rounded px-3 py-2 ${
              errors.back ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.back && (
            <p className="text-red-500 text-sm mt-1">{errors.back}</p>
          )}
        </div>
      </div>
      {errors.upload && (
        <p className="text-red-500 text-center mt-4">{errors.upload}</p>
      )}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-300 text-black rounded"
          disabled={loading}
        >
          Back
        </button>
        <button
          onClick={handleUpload}
          className="px-4 py-2 bg-blue-500 text-white rounded"
          disabled={loading}
        >
          {loading ? "Uploading..." : "Next"}
        </button>
      </div>
    </div>
  );
};

export default LicenseUploadStep;
